import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ProviderForm from "./ProviderForm";
import { emptyProvider, emptyPlanPolicy } from "./utils/providersSchema";
import {
  createProviderWithPlans,
  getProviderById,
  updateProviderWithPlans,
} from "./utils/providerNetworkApi";
import "../../../css/ProviderNetwork/ProviderNetwork.css";
import Sidebar from "../../DesignSystem/Sidebar";
import { Helmet } from "react-helmet-async";
import { useUserEmail } from "../../SignIn/UserEmailContext";

const AddProviderPage = () => {
  const navigate = useNavigate();
  const { providerId } = useParams();
  const { userEmail } = useUserEmail();

  const isEditMode = Boolean(providerId);

  const [provider, setProvider] = useState(emptyProvider);
  const [providerLogoFile, setProviderLogoFile] = useState(null);
  const [plans, setPlans] = useState([
    {
      ...emptyPlanPolicy,
      id: crypto.randomUUID(),
    },
  ]);

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEditMode);

  useEffect(() => {
    const loadProvider = async () => {
      if (!providerId) return;

      setLoading(true);

      try {
        // console.log("[AddProviderPage] Edit mode providerId:", providerId);

        const data = await getProviderById(providerId);

        // console.log("[AddProviderPage] Provider loaded for edit:", data);

        setProvider({
          providerName: data.providerName || "",
          providerLogoUrl: data.providerLogoUrl || "",
          providerLogoPath: data.providerLogoPath || "",
          planCount: data.planCount || data.plans?.length || 0,
        });

        const loadedPlans = data.plans?.length
          ? data.plans.map((plan) => ({
              ...emptyPlanPolicy,
              ...plan,

              // Keep Firestore doc id so update edits the same plan
              id: plan.id,

              // New uploads only. Existing saved attachments stay in attachments.
              attachmentFiles: [],

              // Backwards compatibility for old single fields
              addresses: plan.addresses?.length
                ? plan.addresses
                : plan.address
                  ? [plan.address]
                  : [""],

              emails: plan.emails?.length
                ? plan.emails
                : plan.email
                  ? [plan.email]
                  : [""],

              contacts: plan.contacts?.length
                ? plan.contacts
                : plan.contact
                  ? [plan.contact]
                  : [{ countryCode: "+44", number: "" }],

              websites: plan.websites?.length
                ? plan.websites
                : plan.website
                  ? [plan.website]
                  : [""],
            }))
          : [
              {
                ...emptyPlanPolicy,
                id: crypto.randomUUID(),
              },
            ];

        // console.log("[AddProviderPage] Plans loaded for edit:", loadedPlans);

        setPlans(loadedPlans);
      } catch (err) {
        console.error("[AddProviderPage] Failed to load provider:", err);
        alert(err.message || "Failed to load provider.");
      } finally {
        setLoading(false);
      }
    };

    loadProvider();
  }, [providerId]);

  const validate = () => {
    if (!provider.providerName?.trim()) {
      alert("Provider name is required.");
      return false;
    }

    const validPlans = plans.filter((plan) => plan.planName?.trim());

    if (!validPlans.length) {
      alert("At least one Plan/Policy Name is required.");
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;

    setSaving(true);

    try {
      if (isEditMode) {
        // console.log("[AddProviderPage] Updating provider:", {
        //   providerId,
        //   provider,
        //   plans,
        // });

        await updateProviderWithPlans({
          providerId,
          provider,
          providerLogoFile,
          plans,
          performedBy: userEmail,
        });

        navigate(`/provider-index/${providerId}`);
        return;
      }

      // console.log("[AddProviderPage] Creating new provider:", {
      //   provider,
      //   plans,
      // });

      const result = await createProviderWithPlans({
        provider,
        providerLogoFile,
        plans,
        performedBy: userEmail,
      });

      navigate(`/provider-index/${result.providerId || ""}`);
    } catch (err) {
      console.error("[AddProviderPage] Failed to save provider:", err);
      alert(err.message || "Failed to save provider.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="main-content">
      <Helmet>
        <title>{isEditMode ? "Edit Provider" : "Add Provider"}</title>
      </Helmet>

      <Sidebar />

      <div className="Main-Container">
        {loading ? (
          <div className="provider-network-add-page">
            <div className="provider-network-empty">Loading provider...</div>
          </div>
        ) : (
          <ProviderForm
            mode={isEditMode ? "edit" : "add"}
            provider={provider}
            setProvider={setProvider}
            providerLogoFile={providerLogoFile}
            setProviderLogoFile={setProviderLogoFile}
            plans={plans}
            setPlans={setPlans}
            onSave={handleSave}
            saving={saving}
          />
        )}
      </div>
    </div>
  );
};

export default AddProviderPage;
